import { css } from '@emotion/react';
import { useAtomValue } from 'jotai';
import { Suspense, useMemo } from 'react';
import { SectionPlaceholder } from '../../../../components/SectionPlaceholder/SectionPlaceholder';
import { NoDataMessage2 } from '../../../CompanyProfiles/components/Messages/HelpMessage';
import { SummarySection } from '../SummarySection';
import { MetricsTransactionDataModel } from '../../../../schemas/MetricsTransaction.schema';
import { aggregateTotals, IEquityLot, IEquityTotals } from './equity-holdings-model';
import { IRoundTerms } from './security-story-model';
import { EquityHoldingsKpis } from './EquityHoldingsKpis';
import { EquityHoldingsToolbar } from './EquityHoldingsToolbar';
import { SecurityHistoryGrid } from './SecurityHistoryGrid';
import { SecurityOverviewGrid } from './SecurityOverviewGrid';
import { EquityHoldingsView, equityHoldingsViewJ, useEquityHoldings } from './state';

const placeholder = css`
  margin: 0 0.5rem 0.5rem;
`;

interface IEquityHoldingsPanelProps {
  companyId: number;
}

/**
 * The FD-4344 Equity & Ownership Holdings card: two views over the company's lots, filtered by fund
 * and as-of date, above a KPI strip and a table.
 */
export function EquityHoldingsPanel({ companyId }: IEquityHoldingsPanelProps) {
  return (
    <SummarySection title='Equity & Ownership Holdings'>
      <Suspense fallback={<SectionPlaceholder css={placeholder} label='Loading holdings…' />}>
        <PanelBody companyId={companyId} />
      </Suspense>
    </SummarySection>
  );
}

function PanelBody({ companyId }: IEquityHoldingsPanelProps) {
  const view = useAtomValue(equityHoldingsViewJ(companyId));
  const { lots, heldLots, fundIds, isLoading, hasError, retry, transactionsByLot, roundTerms } =
    useEquityHoldings(companyId);

  // Overview lists held lots, History every lot. Totalling the rows the active view shows is what
  // keeps the KPI strip and the pinned Total row — one above the other — reporting the same figures.
  const rows = view === 'overview' ? heldLots : lots;
  const totals = useMemo(() => aggregateTotals(rows), [rows]);

  return (
    <>
      <EquityHoldingsToolbar companyId={companyId} fundIds={fundIds} />
      <EquityHoldingsKpis totals={totals} />
      <Body
        hasError={hasError}
        heldLots={heldLots}
        isLoading={isLoading}
        lots={lots}
        retry={retry}
        roundTerms={roundTerms}
        totals={totals}
        transactionsByLot={transactionsByLot}
        view={view}
      />
    </>
  );
}

interface IBodyProps {
  hasError: boolean;
  heldLots: IEquityLot[];
  isLoading: boolean;
  lots: IEquityLot[];
  retry: () => void;
  roundTerms: Map<number, IRoundTerms>;
  /** Totals of exactly the rows this body renders. */
  totals: IEquityTotals;
  transactionsByLot: Map<string, MetricsTransactionDataModel[]>;
  view: EquityHoldingsView;
}

/**
 * The empty states are kept distinct on purpose. "No lots at all" and "lots exist but none is still
 * held" mean different things — the second is the normal state for a fully exited position, and its
 * rows are in Security History — and a failed fetch must not read as "no data".
 */
function Body({
  hasError,
  heldLots,
  isLoading,
  lots,
  retry,
  roundTerms,
  totals,
  transactionsByLot,
  view,
}: IBodyProps) {
  if (isLoading) {
    return <SectionPlaceholder css={placeholder} label='Loading holdings…' />;
  }
  if (hasError) {
    // A rejected fetch is cached per request key, so without an explicit retry the card stays broken
    // for the rest of the session.
    return <SectionPlaceholder css={placeholder} label='Holdings could not be loaded.' onRetry={retry} />;
  }
  if (lots.length === 0) {
    return <NoDataMessage2 message='No holdings as of this date' />;
  }
  if (view === 'history') {
    return (
      <SecurityHistoryGrid
        lots={lots}
        roundTerms={roundTerms}
        totals={totals}
        transactionsByLot={transactionsByLot}
      />
    );
  }
  if (heldLots.length === 0) {
    return (
      <NoDataMessage2
        message={`Nothing currently held as of this date. ${lots.length} realized, converted or written-off ${
          lots.length === 1 ? 'position' : 'positions'
        } will appear in Security History.`}
      />
    );
  }

  return <SecurityOverviewGrid heldLots={heldLots} totals={totals} />;
}
